import React, { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import ABCDExercise from './ABCDExercise';
import './ABCDExerciseEmbed.css';
import { useExerciseSync } from '../../context/ExerciseSyncContext';

const ABCD_ICON = (
  <span className="abcd-icon-spacing" style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #8a2be2 0%, #a855f7 100%)'
  }}>
    <svg width="28" height="28" viewBox="0 0 512 512" fill="white">
      <g>
        <path d="m0 241h241v-241h-231c-5.523 0-10 4.477-10 10zm55.88-61.75 48.13-126.37c.05-.12.09-.24.14-.35 6.058-14.817 27.053-14.816 33.13-.03.05.14.11.27.16.41l47.7 126.39c2.92 7.75-.99 16.4-8.74 19.33-7.98 2.964-16.474-1.157-19.33-8.74-2.588-6.858-.887-2.353-8.22-21.77h-56.62c-7.376 19.343-5.698 14.938-8.31 21.81-2.95 7.74-11.62 11.63-19.36 8.68s-11.63-11.61-8.68-19.36z"/>
        <path d="m271 0v241h241v-231c0-5.523-4.477-10-10-10zm125.12 199.44c-8.977 0-43.74.15-43.74.15-8.285 0-15-6.726-15-15v-128.18c0-8.29 6.72-15 15-15h37.23c35.677 0 56.764 40.06 36.84 69.43 11.66 9.07 19.17 23.22 19.17 39.1 0 27.29-22.21 49.5-49.5 49.5z"/>
        <path d="m10 512h231v-241h-241v231c0 5.523 4.477 10 10 10zm166.59-186.08c6.86 4.64 8.67 13.97 4.03 20.83-4.649 6.873-13.946 8.668-20.83 4.03-32.257-21.796-76.52 1.283-76.52 40.72 0 42.363 49.706 62.049 74.29 40.16 3.899-3.476 6.501-8.785 14.48-9.14 12.923-.641 20.621 14.579 12.18 24.62-40.696 48.713-130.95 17.344-130.95-55.64 0-63.402 71.16-100.805 123.32-65.58z"/>
        <path d="m271 512h231c5.523 0 10-4.477 10-10v-231h-241zm77.77-197.7h35.23c39.02 0 65.23 31.03 65.23 77.2 0 20.23-5.81 38.96-16.38 52.74-11.69 15.26-28.58 23.85-47.56 24.18-8.983.152-35.255.28-36.27.28-7.893 0-14.494-6.152-14.97-14.09v-.05c-.02-.27-.03-.54-.03-.81 0 0-.25-71.621-.25-124.32 0-8.674 6.948-15.13 15-15.13z"/>
        <path d="m120.67 93.43-17.02 44.69h33.88z"/>
        <path d="m384.77 438.42c23.81-.41 34.46-23.86 34.46-46.92 0-22.82-9.25-47.2-35.23-47.2h-20.2c.084 64.107.067 64.113.17 94.32 7.52-.05 16.03-.11 20.8-.2z"/>
        <path d="m404.11 85.9c0-7.99-6.5-14.49-14.5-14.49h-22.23v29.02.04c2.785-.009 14.439-.07 22.23-.07 8 0 14.5-6.5 14.5-14.5z"/>
        <path d="m396.12 130.43c-10.073 0-17.998 0-28.74 0v39.1c10.48-.04 23.49-.09 28.74-.09 10.75 0 19.5-8.75 19.5-19.5 0-10.76-8.75-19.51-19.5-19.51z"/>
      </g>
    </svg>
  </span>
);

const ABCDExerciseEmbed = ({ node, updateAttributes, editor, getPos }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const exerciseIdRef = React.useRef(node.attrs.exerciseId);
  if (!exerciseIdRef.current) exerciseIdRef.current = crypto.randomUUID();
  const exerciseId = exerciseIdRef.current;
  useEffect(() => {
    if (!node.attrs.exerciseId) updateAttributes({ exerciseId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { myState, otherState, updateState, liveUpdateCount } = useExerciseSync(exerciseId);
  // Force ProseMirror to re-measure after expand/collapse so click coordinates stay accurate
  useEffect(() => {
    if (!editor) return;
    const id = requestAnimationFrame(() => {
      try {
        editor.view.updateState(editor.view.state);
        // Mobile Safari/Chrome: re-syncing the view can leave the browser
        // thinking the (still logically-selected) editable region wants the
        // on-screen keyboard, even though the user only tapped this
        // exercise's own contentEditable=false UI. Blur ONLY if the outer
        // editor root itself still has focus (not an input inside the
        // exercise's own edit mode), so the keyboard does not pop on
        // expand/collapse.
        if (typeof document !== 'undefined' && document.activeElement === editor.view.dom) {
          editor.view.dom.blur();
        }
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror a REMOTE peer's expand/collapse live, without replaying stale
  // localStorage-hydrated data on every remount — see ExerciseSyncContext's
  // liveUpdateCount for why this can't just watch otherState.expanded alone.
  const lastLiveUpdateCountRef = React.useRef(liveUpdateCount);
  useEffect(() => {
    if (liveUpdateCount === lastLiveUpdateCountRef.current) return;
    lastLiveUpdateCountRef.current = liveUpdateCount;
    if (otherState.expanded !== undefined && otherState.expanded !== isExpanded) {
      setIsExpanded(otherState.expanded);
    }
  }, [liveUpdateCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const { questions, title, level, homework, passage } = node.attrs;

  // eslint-disable-next-line no-unused-vars
  const iconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #8a2be2 0%, #a855f7 100%)',
    marginRight: 20,
  };

  if (!questions || !Array.isArray(questions)) {
    return (
      <NodeViewWrapper>
        <div className="abcd-embed-error">
          <p>Error: Invalid ABCD exercise data</p>
        </div>
      </NodeViewWrapper>
    );
  }

  const insertParagraphAt = (pos) => {
    if (!editor || typeof pos !== 'number') return;
    editor.chain().focus().insertContentAt(pos, { type: 'paragraph' }).setTextSelection(pos + 1).run();
  };

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = typeof getPos === 'function' ? getPos() : null;
    insertParagraphAt(pos);
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const base = typeof getPos === 'function' ? getPos() : null;
    if (typeof base === 'number' && node && typeof node.nodeSize === 'number') {
      insertParagraphAt(base + node.nodeSize);
    }
  };

  const isHomework = (() => {
    try {
      // Prefer editor root attribute if present
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-tab-type');
      if (fromRoot) return fromRoot === 'homework';
      // Fallback to body attribute
      const fromBody = typeof document !== 'undefined' ? document.body.getAttribute('data-novas-current-tab-type') : null;
      return fromBody === 'homework';
    } catch (_) { return false; }
  })();

  const isStudent = (() => {
    try {
      // Check if we're in student mode by looking for student-specific attributes
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-student-mode');
      if (fromRoot) return fromRoot === 'true';
      
      // Check if we're in a student dashboard context
      const isStudentDashboard = typeof document !== 'undefined' && 
        (window.location.pathname.includes('/student-dashboard') || 
         window.location.pathname.includes('/student-profile'));
      
      return isStudentDashboard;
    } catch (_) { return false; }
  })();

  // Drag handlers for visual feedback
  const handleDragStart = (e) => {
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'scale(0.95)';
    e.target.style.outline = 'none';
    e.target.style.border = 'none';
    e.target.style.boxShadow = 'none';
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    e.target.style.transform = '';
    e.target.style.outline = '';
    e.target.style.border = '';
    e.target.style.boxShadow = '';
    e.target.classList.remove('dragging');
  };

  // Only mark the outer wrapper / preview as a drag handle when the embed
  // is collapsed. When expanded, ProseMirror was treating clicks on the
  // editing inputs as node-selection / drag-start gestures — the input
  // lost focus, the page scrolled to the node anchor, and the embed
  // collapsed back to preview. Same fix as WordTranslationEmbed.
  const dragProps = isExpanded
    ? {}
    : { draggable: true, 'data-drag-handle': '', onDragStart: handleDragStart, onDragEnd: handleDragEnd };

  return (
    <NodeViewWrapper as="div" className="abcd-node-view" contentEditable={false} {...dragProps}>
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div className="abcd-exercise-embed" {...dragProps}>
        {!isExpanded ? (
          <div className="embed-preview unified-exercise-preview" onClick={() => { setIsExpanded(true); updateState({ expanded: true }); }} {...dragProps}>
            {ABCD_ICON}
            <div className="unified-preview-main">
              <div className="unified-preview-title">{title || 'ABCD Exercise'}</div>
              <div className="unified-preview-meta">
                {questions.length} questions · Level: {level || 'B1'}
              </div>
            </div>
            <div className="unified-preview-actions" onClick={e => e.stopPropagation()}>
              <button
                className="unified-icon-btn"
                title="Add to homework"
                style={{ display: (isHomework || isStudent) ? 'none' : 'inline-flex' }}
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('novas:add-to-homework', {
                      detail: {
                        type: 'abcd',
                        attrs: { questions, title, level, passage }
                      }
                    }));
                  } catch (_) {}
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h4a.5.5 0 1 0 0-1h-4a.5.5 0 0 1-.5-.5V7.207l5-5 6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293z"/>
                  <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-3.5-2a.5.5 0 0 0-.5.5v1h-1a.5.5 0 0 0 0 1h1v1a.5.5 0 1 0 1 0v-1h1a.5.5 0 1 0 0-1h-1v-1a.5.5 0 0 0-.5-.5"/>
                </svg>
              </button>
              <button className="unified-icon-btn" title="Delete" style={{ display: isStudent ? 'none' : 'inline-flex' }} onClick={() => editor?.chain().focus().deleteRange({ from: getPos(), to: getPos() + node.nodeSize }).run()}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0"/>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="abcd-expanded" onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <ABCDExercise
              questions={questions}
              title={title}
              level={level}
              passage={passage}
              onClose={() => { setIsExpanded(false); updateState({ expanded: false }); }}
              updateAttributes={updateAttributes}
              node={node}
              editor={editor}
              getPos={getPos}
              isStudent={isStudent}
              myState={myState}
              otherState={otherState}
              updateState={updateState}
            />
          </div>
        )}
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default ABCDExerciseEmbed; 